﻿import React, { useState, useEffect, useReducer, useCallback } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "UPDATE_USER_PRESENCE") {
    const presence = action.payload;
    const userIndex = state.findIndex((u) => u.id === presence.userId);

    if (userIndex !== -1) {
      state[userIndex] = {
        ...state[userIndex],
        presenceStatus: presence.status,
        lastActivityAt: presence.lastActivityAt,
        lastHeartbeatAt: presence.lastHeartbeatAt,
        lastOfflineAt: presence.lastOfflineAt
      };
    }

    return [...state];
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  statusCell: {
    display: "flex",
    justifyContent: "center"
  },
  statusChip: {
    fontWeight: 700,
    minWidth: 108
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%"
  },
}));

const getPresenceLabel = status => {
  switch (status) {
    case "online":
      return "Online";
    case "away":
      return "Ausente";
    default:
      return "Offline";
  }
};

const getPresenceColors = status => {
  switch (status) {
    case "online":
      return {
        backgroundColor: "rgba(34, 197, 94, 0.12)",
        color: "#15803d",
        dotColor: "#22c55e"
      };
    case "away":
      return {
        backgroundColor: "rgba(245, 158, 11, 0.12)",
        color: "#b45309",
        dotColor: "#f59e0b"
      };
    default:
      return {
        backgroundColor: "rgba(239, 68, 68, 0.12)",
        color: "#b91c1c",
        dotColor: "#ef4444"
      };
  }
};

const Users = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [disconnectingUser, setDisconnectingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const [sipRegistrations, setSipRegistrations] = useState({});
  const [sipAccountsByUserId, setSipAccountsByUserId] = useState({});
  const { user: loggedInUser } = React.useContext(AuthContext);

  const fetchUsers = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const targetPage = reset ? 1 : pageNumber;
      const { data } = await api.get("/users/", {
        params: { searchParam, pageNumber: targetPage },
      });

      if (reset) {
        dispatch({ type: "RESET" });
      }

      dispatch({ type: "LOAD_USERS", payload: data.users });
      setHasMore(data.hasMore);
      if (reset) {
        setPageNumber(1);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, searchParam]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchUsers]);

  const fetchSipRegistrations = useCallback(async () => {
    try {
      const { data } = await api.get("/users/sip/registrations");
      const nextState = {};

      (data?.registrations || []).forEach(item => {
        if (!item?.userId) return;
        nextState[Number(item.userId)] = {
          registered: Boolean(item.registered),
          state: String(item.state || "")
        };
      });

      setSipRegistrations(nextState);
    } catch (err) {
      setSipRegistrations({});
    }
  }, []);

  const fetchSipAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/extensions");
      const nextState = {};

      (data || []).forEach(item => {
        const userId = Number(item?.userId);
        if (!userId) return;
        nextState[userId] = String(item?.extension || "");
      });

      setSipAccountsByUserId(nextState);
    } catch (err) {
      setSipAccountsByUserId({});
    }
  }, []);

  useEffect(() => {
    fetchSipAccounts();
    fetchSipRegistrations();
    const interval = setInterval(() => {
      fetchSipAccounts();
      fetchSipRegistrations();
    }, 12000);

    return () => clearInterval(interval);
  }, [fetchSipAccounts, fetchSipRegistrations]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("user", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    });

    socket.on("userPresence", data => {
      if (data.action === "update" && data.presence) {
        dispatch({ type: "UPDATE_USER_PRESENCE", payload: data.presence });
      }
    });

    socket.on("extension", () => {
      fetchSipAccounts();
      fetchSipRegistrations();
    });

    return () => {
      socket.disconnect();
    };
  }, [fetchSipAccounts, fetchSipRegistrations]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleDisconnectUser = async (userId) => {
    try {
      await api.post(`/users/${userId}/disconnect`);
      toast.success(i18n.t("users.toasts.disconnected"));
    } catch (err) {
      toastError(err);
    } finally {
      setDisconnectingUser(null);
      setDisconnectModalOpen(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={
          disconnectingUser &&
          `${i18n.t("users.confirmationModal.disconnectTitle")} ${
            disconnectingUser.name
          }?`
        }
        open={disconnectModalOpen}
        onClose={setDisconnectModalOpen}
        onConfirm={() => handleDisconnectUser(disconnectingUser.id)}
      >
        {i18n.t("users.confirmationModal.disconnectMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      <MainHeader>
        <Title>{i18n.t("users.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => fetchUsers(true)}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenUserModal}
          >
            {i18n.t("users.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("users.table.name")}</TableCell>
              <TableCell align="center">
                {i18n.t("users.table.email")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("users.table.profile")}
              </TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">
                {i18n.t("users.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("users.table.sipUser")}
              </TableCell>
              <TableCell align="center">SIP Status</TableCell>
              <TableCell align="center">
                {i18n.t("users.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {users.map((user) => {
                const sipAccount = sipAccountsByUserId[user.id] || "";
                const hasSipExtension = Boolean(sipAccount);
                return (
                <TableRow key={user.id}>
                  <TableCell align="center">{user.name}</TableCell>
                  <TableCell align="center">{user.email}</TableCell>
                  <TableCell align="center">{user.profile}</TableCell>
                  <TableCell align="center">
                    <div className={classes.statusCell}>
                      <Chip
                        size="small"
                        className={classes.statusChip}
                        label={
                          <Box display="flex" alignItems="center" gridGap={8}>
                            <span
                              className={classes.statusDot}
                              style={{
                                backgroundColor: getPresenceColors(user.presenceStatus).dotColor
                              }}
                            />
                            <span>{getPresenceLabel(user.presenceStatus)}</span>
                          </Box>
                        }
                        style={{
                          backgroundColor: getPresenceColors(user.presenceStatus).backgroundColor,
                          color: getPresenceColors(user.presenceStatus).color
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell align="center">{user.whatsapp?.name}</TableCell>
                  <TableCell align="center">{sipAccount || "-"}</TableCell>
                  <TableCell align="center">
                    {hasSipExtension ? (
                      <div className={classes.statusCell}>
                        {(() => {
                          const sipState = sipRegistrations[user.id]?.state || "";
                          const sipRegistered = Boolean(
                            sipRegistrations[user.id]?.registered
                          );
                          const isUnavailable = sipState === "unavailable";

                          const label = isUnavailable
                            ? "Indisponível"
                            : sipRegistered
                            ? "Registrado"
                            : "Offline";

                          const dotColor = isUnavailable
                            ? "#f59e0b"
                            : sipRegistered
                            ? "#22c55e"
                            : "#ef4444";

                          const backgroundColor = isUnavailable
                            ? "rgba(245, 158, 11, 0.12)"
                            : sipRegistered
                            ? "rgba(34, 197, 94, 0.12)"
                            : "rgba(239, 68, 68, 0.12)";

                          const color = isUnavailable
                            ? "#b45309"
                            : sipRegistered
                            ? "#15803d"
                            : "#b91c1c";

                          return (
                        <Chip
                          size="small"
                          className={classes.statusChip}
                          label={
                            <Box display="flex" alignItems="center" gridGap={8}>
                              <span
                                className={classes.statusDot}
                                style={{ backgroundColor: dotColor }}
                              />
                              <span>{label}</span>
                            </Box>
                          }
                          style={{ backgroundColor, color }}
                        />
                          );
                        })()}
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {Number(user.id) !== Number(loggedInUser?.id) && (
                      <IconButton
                        size="small"
                        title={i18n.t("users.buttons.disconnect")}
                        onClick={() => {
                          setDisconnectingUser(user);
                          setDisconnectModalOpen(true);
                        }}
                      >
                        <PowerSettingsNewIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleEditUser(user)}
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setConfirmModalOpen(true);
                        setDeletingUser(user);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
              })}
              {loading && <TableRowSkeleton columns={8} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Users;

